// ============================================================================
// workingAs.js (Oct 22, 2026): "Working as". An admin logged in at the warehouse
// can pick a worker, and everything they process is logged under that worker
// (billed by, worked by, packed by, Tally Board), not under the admin.
//
// How it works: every station gets "who is doing this" from useWarehouseSession()
// in router/index.js. That calls applyWorkingAs(), which swaps in the chosen
// worker's name and ID. Everything else in the session stays the admin's own
// (admin access, the station's warehouse location, the Firebase login).
//
// It only applies when:
//   - the logged-in person is an admin,
//   - it was chosen by this same admin login on this device,
//   - and it was chosen today (it switches back to the admin automatically the next day).
// Logging out clears it.
// ============================================================================
const KEY = 'warehouseWorkingAs'

const today = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function readRaw() {
  try {
    const s = localStorage.getItem(KEY)
    return s ? JSON.parse(s) : null
  } catch {
    return null
  }
}

/** The worker this admin is working as right now, or null. */
export function getWorkingAs(session) {
  if (!session || session.isAdmin !== true) return null
  const w = readRaw()
  if (!w || !w.workerName) return null
  if (w.forDocId !== (session.workerDocId || null) || w.day !== today()) {
    clearWorkingAs()
    return null
  }
  return w
}

/** Start working as a worker ({ workerId, workerName, workerDocId }). */
export function setWorkingAs(worker, session) {
  if (!session || session.isAdmin !== true || !worker) return
  try {
    localStorage.setItem(KEY, JSON.stringify({
      workerId: worker.workerId ?? null,
      workerName: worker.workerName || '',
      workerDocId: worker.workerDocId || null,
      forDocId: session.workerDocId || null,
      day: today(),
    }))
  } catch (e) {
    console.warn('[workingAs] could not save', e)
  }
}

export function clearWorkingAs() {
  try { localStorage.removeItem(KEY) } catch (e) { /* ignore */ }
}

/**
 * The session the stations see. Same object as before unless an admin picked
 * a worker; then workerId/workerName are that worker's, and realWorkerId /
 * realWorkerName keep the admin's own.
 */
export function applyWorkingAs(session) {
  const w = getWorkingAs(session)
  if (!w) return session
  return {
    ...session,
    workerId: w.workerId,
    workerName: w.workerName,
    realWorkerId: session.workerId,
    realWorkerName: session.workerName,
    workingAs: true,
  }
}
